import React from "react";
import { Download, FileArchive, ChevronDown, ChevronUp, Trash2, Image as ImageIcon, ArrowUp, ArrowDown, AlertTriangle, Save } from "lucide-react";
import { api } from "../../lib/api";
import { useCarouselDocument } from "./useCarouselDocument";
import { TypographyControls, NumberControl, TEXT_ROLES } from "./TypographyControls";
import { CarouselFonts, CarouselPreview } from "./CarouselPreview";
import "./carousel-editor.css";

const formatLabels = { post_4x5: "Post · 4:5", square_1x1: "Carré · 1:1", story_9x16: "Story · 9:16" };
const absUrl = url => url?.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
const legacy = (slide, role) => {
  const style = slide.style || {};
  const result = {};
  ['font', 'size', 'color', 'opacity'].forEach(key => {
    if (style[`${role}_${key}`] != null) result[key] = style[`${role}_${key}`];
  });
  if (style.text_align) result.align = style.text_align;
  return result;
};

export const CarouselEditor = ({ carousel, layouts, formats, typographyDefaults, mediaByCategory, onReload }) => {
  const { document: doc, change, save, discard, status, error } = useCarouselDocument(carousel);
  const [open, setOpen] = React.useState(true);
  const [selected, setSelected] = React.useState(() => sessionStorage.getItem(`carousel-page-${carousel.id}`) || doc.slides[0]?.id);
  const [role, setRole] = React.useState('title');
  const [scope, setScope] = React.useState('individual');
  const [panel, setPanel] = React.useState('typography');
  const [scene, setScene] = React.useState(null);
  const [working, setWorking] = React.useState('');
  const [actionError, setActionError] = React.useState('');
  const [photoPicker, setPhotoPicker] = React.useState(false);
  const slide = doc.slides.find(s => s.id === selected) || doc.slides[0];
  const id = `ce-${carousel.id}`;
  const globals = doc.global_typography || {};
  const selectPage = sid => { setSelected(sid); sessionStorage.setItem(`carousel-page-${carousel.id}`, sid); setScene(null); };
  const patchSlide = patch => change(current => ({ ...current, slides: current.slides.map(s => s.id === slide.id ? { ...s, ...patch } : s) }));
  const currentStyle = {
    ...typographyDefaults[role],
    ...(scope === 'individual' ? legacy(slide, role) : {}),
    ...(globals[role] || {}),
    ...(scope === 'individual' ? slide.typography?.[role] || {} : {}),
  };
  currentStyle.resolvedColor = ['editorial_cover', 'editorial_quote'].includes(slide.layout) ? '#F3EEE5' : '#191B20';
  const setStyle = patch => {
    if (scope === 'global') change(current => ({ ...current, global_typography: { ...current.global_typography,
      [role]: { ...current.global_typography?.[role], ...patch } } }));
    else patchSlide({ typography: { ...slide.typography, [role]: { ...slide.typography?.[role], ...patch } } });
  };
  const resetStyle = () => {
    if (scope === 'global') {
      const next = { ...globals }; delete next[role]; change(current => ({ ...current, global_typography: next }));
    } else {
      const next = { ...slide.typography }; delete next[role];
      const old = { ...slide.style };
      Object.keys(old).filter(k => k.startsWith(`${role}_`)).forEach(k => delete old[k]);
      // Preserve the alignment of other legacy categories while resetting this one.
      if (old.text_align) {
        Object.keys(TEXT_ROLES).filter(k => k !== role).forEach(k => { next[k] = { align: old.text_align, ...next[k] }; });
        delete old.text_align;
      }
      patchSlide({ typography: next, style: old });
    }
  };
  const composition = { capacity: .85, padding: 48, gap: 18, ...slide.composition };
  const field = role === 'page_number' ? 'page_label' : role;
  const textValue = role === 'bullets' ? (slide.bullets || []).join('\n') : slide[field] ?? '';
  const contentPlaceholder = role === 'page_number' ? `${slide.index + 1} / ${doc.slides.length}` : role === 'eyebrow' ? 'Chapitre automatique' : TEXT_ROLES[role];

  const action = async (key, fn) => {
    setWorking(key); setActionError('');
    try { await fn(); } catch (e) {
      let detail = e?.response?.data?.detail;
      if (e?.response?.data instanceof Blob) { try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* non-JSON */ } }
      setActionError(typeof detail === 'string' ? detail : 'Opération impossible. Vérifiez l’enregistrement et la mise en page.');
    } finally { setWorking(''); }
  };
  const download = (kind, fmt) => action('export', async () => {
    await save();
    const path = kind === 'zip' ? 'download' : `slides/${slide.id}/export?fmt=${fmt}`;
    const response = await api.get(`/admin/marketing/carousels/${doc.id}/${path}`, { responseType: 'blob' });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a'); link.href = url;
    link.download = kind === 'zip' ? `carrousel-${doc.theme_key}.zip` : `studioeuclides-${slide.index + 1}.${fmt}`;
    link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  });
  const reorder = direction => change(current => {
    const slides = [...current.slides]; const index = slides.findIndex(s => s.id === slide.id);
    const target = index + direction;
    if (target < 0 || target >= slides.length) return current;
    [slides[index], slides[target]] = [slides[target], slides[index]];
    return { ...current, slides: slides.map((s, i) => ({ ...s, index: i })) };
  });
  const compact = () => {
    const typography = { ...slide.typography };
    Object.keys(TEXT_ROLES).forEach(key => {
      const effective = { ...typographyDefaults[key], ...legacy(slide, key), ...globals[key], ...typography[key] };
      typography[key] = { ...typography[key], line_height: Math.max(1, Math.round(effective.line_height * .85 * 100) / 100) };
    });
    patchSlide({ typography, composition: { ...composition, gap: Math.max(0, composition.gap - 8) } });
  };
  const photoList = Object.values(mediaByCategory || {}).flat();

  return <article className="carousel-editor" data-testid={`carousel-card-${doc.id}`}>
    <CarouselFonts/>
    <header className="carousel-editor-header">
      <button className="carousel-heading-toggle" data-testid={`${id}-toggle`} onClick={() => setOpen(v => !v)} aria-expanded={open}>
        {open ? <ChevronUp size={18}/> : <ChevronDown size={18}/>}<span>{doc.theme_label}</span>
      </button>
      <span className={`carousel-save-state ${status}`} data-testid={`${id}-save-status`} aria-live="polite">
        {status === 'saved' ? 'Enregistré' : status === 'error' ? 'Brouillon local · non enregistré' : 'Enregistrement…'}
      </span>
      <button data-testid={`${id}-download-zip`} title="Télécharger le carrousel" disabled={!!working} onClick={() => download('zip')}><FileArchive size={16}/><span>ZIP</span></button>
      <button data-testid={`${id}-delete`} title="Supprimer le carrousel" aria-label="Supprimer le carrousel" disabled={!!working} onClick={() => {
        if (window.confirm('Supprimer ce carrousel ?')) action('delete', async () => { await save(); await api.delete(`/admin/marketing/carousels/${doc.id}`); await onReload(); });
      }}><Trash2 size={16}/></button>
    </header>
    {(error || actionError) && <div className="carousel-alert" data-testid={`${id}-error`} role="alert">
      {error || actionError}
      {error && <div className="carousel-alert-actions"><button data-testid={`${id}-retry-save`} onClick={() => action('save', save)}>Réessayer</button>
        <button data-testid={`${id}-reload-server`} onClick={() => {
          if (window.confirm('Abandonner ce brouillon local et charger la version enregistrée ?')) action('reload', discard);
        }}>Charger la version enregistrée</button></div>}
    </div>}
    {open && <>
      <nav className="carousel-page-nav" aria-label="Pages du carrousel">
        {doc.slides.map((s, i) => <button data-testid={`${id}-page-${i + 1}`} key={s.id} aria-current={s.id === slide.id ? 'page' : undefined}
          onClick={() => selectPage(s.id)}><span>{String(i + 1).padStart(2, '0')}</span><span>{s.title || 'Sans titre'}</span></button>)}
      </nav>
      <div className="carousel-editor-grid">
        <section className="carousel-preview-column">
          <CarouselPreview key={slide.id} id={id} slide={slide} format={doc.format} total={doc.slides.length} globals={globals}
            onSelect={key => { setRole(key); setScope('individual'); setPanel('typography'); }} onScene={setScene}/>
          <div className="carousel-preview-toolbar">
            <select data-testid={`${id}-format`} aria-label="Format du carrousel" value={doc.format} onChange={e => change(current => ({ ...current, format: e.target.value }))}>
              {formats.map(f => <option key={f.key} value={f.key}>{formatLabels[f.key] || f.key}</option>)}
            </select>
            <button data-testid={`${id}-move-up`} aria-label="Reculer cette page" title="Reculer cette page" disabled={slide.index === 0} onClick={() => reorder(-1)}><ArrowUp size={16}/></button>
            <button data-testid={`${id}-move-down`} aria-label="Avancer cette page" title="Avancer cette page" disabled={slide.index === doc.slides.length - 1} onClick={() => reorder(1)}><ArrowDown size={16}/></button>
            {['jpg', 'png'].map(fmt => <button data-testid={`${id}-export-${fmt}`} key={fmt} disabled={!!working || !scene?.valid}
              onClick={() => download('slide', fmt)}><Download size={15}/>{fmt.toUpperCase()}</button>)}
          </div>
          {scene && !scene.valid && <div className="carousel-alert" role="alert" data-testid={`${id}-overflow-warning`}>
            <strong><AlertTriangle size={17}/> Espace insuffisant</strong>
            <p>La taille demandée est conservée. L’export de cette page est suspendu pour éviter toute coupure ou superposition.</p>
            <div className="carousel-alert-actions">
              <button data-testid={`${id}-overflow-expand`} onClick={() => patchSlide({ composition: { ...composition, capacity: .94 } })}>Agrandir la zone texte</button>
              <button data-testid={`${id}-overflow-compact`} onClick={compact}>Réduire les espacements</button>
              <button data-testid={`${id}-overflow-manual`} onClick={() => setPanel('layout')}>Ajuster la mise en page</button>
            </div>
          </div>}
          {scene?.warnings?.length > 0 && <ul className="carousel-readability" data-testid={`${id}-readability-warnings`}>
            {scene.warnings.map((warning, i) => <li data-testid={`${id}-warning-${warning.role}-${warning.code}`} key={i}>{warning.message}</li>)}
          </ul>}
        </section>
        <aside className="carousel-controls-column">
          <div className="carousel-panel-tabs" role="tablist">
            {[['typography', 'Texte & style'], ['layout', 'Mise en page']].map(([key, label]) => <button key={key} role="tab"
              data-testid={`${id}-panel-${key}`} aria-selected={panel === key} onClick={() => setPanel(key)}>{label}</button>)}
          </div>
          {panel === 'typography' && <>
            {scope === 'individual' && <label className="carousel-content-field" htmlFor={`${id}-content`}>{TEXT_ROLES[role]}
              <textarea id={`${id}-content`} data-testid={`${id}-content-${role}`} value={textValue} placeholder={contentPlaceholder} rows={role === 'bullets' ? 4 : 2}
                onChange={e => patchSlide({ [field]: role === 'bullets' ? e.target.value.split('\n') : e.target.value })}/>
            </label>}
            <TypographyControls id={id} role={role} setRole={setRole} scope={scope} setScope={setScope} style={currentStyle}
              onChange={setStyle} onReset={resetStyle} overridden={Object.keys(slide.typography?.[role] || {}).length > 0 || Object.keys(legacy(slide, role)).length > 0}/>
          </>}
          {panel === 'layout' && <section className="carousel-layout-panel" data-testid={`${id}-layout-panel`}>
            <label htmlFor={`${id}-layout`}>Composition<select id={`${id}-layout`} data-testid={`${id}-layout-select`} value={slide.layout} onChange={e => patchSlide({ layout: e.target.value })}>
              {layouts.map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
            </select></label>
            <NumberControl id={`${id}-capacity`} label="Zone texte maximale" unit="%" value={Math.round(composition.capacity * 100)} min={25} max={94}
              onChange={value => patchSlide({ composition: { ...composition, capacity: value / 100 } })}/>
            <NumberControl id={`${id}-gap`} label="Espace entre les blocs" unit="px" value={composition.gap} min={0} max={120}
              onChange={value => patchSlide({ composition: { ...composition, gap: value } })}/>
            <NumberControl id={`${id}-padding`} label="Marges intérieures" unit="px" value={composition.padding} min={12} max={120}
              onChange={value => patchSlide({ composition: { ...composition, padding: value } })}/>
            <button data-testid={`${id}-pick-photo`} onClick={() => setPhotoPicker(v => !v)}><ImageIcon size={17}/> Changer la photo</button>
            {photoPicker && <div className="carousel-photo-picker" data-testid={`${id}-photo-picker`}>
              {photoList.map(photo => <button key={photo.id} data-testid={`${id}-photo-${photo.id}`} title={photo.label || 'Photo studio'} onClick={() => {
                patchSlide({ source_image_url: photo.url }); setPhotoPicker(false);
              }}><img src={absUrl(photo.url)} alt={photo.label || 'Photo studio'}/></button>)}
              {!photoList.length && <p data-testid={`${id}-no-photos`}>Aucune photo dans la bibliothèque.</p>}
            </div>}
          </section>}
          <button className="carousel-save-button" data-testid={`${id}-save`} disabled={!!working || status === 'saving'} onClick={() => action('save', save)}><Save size={16}/> Enregistrer</button>
        </aside>
      </div>
    </>}
  </article>;
};